import { useCallback, useState } from 'react';

import { IPaymentDetails, RequestPaymentFn, paymentErrorType } from 'features/payment/types';
import { STORE_UNAVAILABLE } from 'features/store/constants';

import { IUseApplePay } from 'state/apple-pay/hooks/types';
import useApplePay, { IRequestApplePayPayment } from 'state/apple-pay/hooks/use-apple-pay';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { logger } from 'utils/logger';

export const useRequestApplePayPayment: RequestPaymentFn = ({ applePay: applePayInput }) => {
  const { billingCountry = ISOs.USA, tip, isForPrepaidReload, serverOrder, reloadAmount } =
    applePayInput ?? {};
  const [data, setData] = useState<IPaymentDetails>();
  const [error, setError] = useState<paymentErrorType>();
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);

  const handleApplePay: IUseApplePay['handleApplePay'] = async ({
    data: applePayDetails,
    error,
  }) => {
    if (error) {
      // TODO: log here
      setError(paymentErrorType.applePayGeneric);
      return;
    }
    setData({ applePayDetails });
  };

  const {
    requestApplePayPayment,
    formatApplePayCartPayload,
    requestingPayment: isLoading,
  } = useApplePay({
    handleApplePay,
    tipAmount: tip,
    prepaid: isForPrepaidReload,
    serverOrder,
  });

  const requestPayment = useCallback(() => {
    const applePayParams: IRequestApplePayPayment | null = isForPrepaidReload
      ? {
          countryCode,
          currencyCode,
          total: reloadAmount ?? 0,
        }
      : formatApplePayCartPayload();
    if (!applePayParams) {
      logger.error({
        message:
          '[useRequestApplePayPayment][requestPayment] Apple Pay params could not be generated.',
        isForPrepaidReload,
        reloadAmount,
        billingCountry,
        tip,
        serverOrder,
      });
      setError(paymentErrorType.applePayGeneric);

      return;
    }
    try {
      requestApplePayPayment(applePayParams);
    } catch (error) {
      logger.error({
        message:
          '[useRequestApplePayPayment][requestPayment] Error occurred while requesting Apple Pay payment.',
        error,
      });
      // TODO: Log any other error here, note that this may not be required
      // anymore so we can refactor the apple pay hook to remove this check
      if (error instanceof Error && error.message === STORE_UNAVAILABLE) {
        setError(paymentErrorType.storeUnavailable);
      } else {
        setError(paymentErrorType.applePayGeneric);
      }
    }
  }, [
    billingCountry,
    countryCode,
    currencyCode,
    formatApplePayCartPayload,
    isForPrepaidReload,
    reloadAmount,
    requestApplePayPayment,
    serverOrder,
    tip,
  ]);

  return { data, isLoading, error, requestPayment };
};
